import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { Tender } from "@/types/tenders";

type Props = {
  tender: Tender;
  onClose: () => void;
  onRequestSupport?: (reference: string) => void;
}

function formatBudget(budget: number | null): string {
  if (budget == null) return "—";
  if (budget >= 1_000_000) return `$${(budget / 1_000_000).toFixed(1)}M`;
  return `$${budget.toLocaleString()}`;
}

export default function TenderDetailModal({ tender, onClose, onRequestSupport }: Props) {
  const { t } = useTranslation();

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", esc); document.body.style.overflow = ""; };
  }, [onClose]);

  const statusColor: Record<string, string> = {
    "Open": "bg-green-100 text-green-700",
    "Closing Soon": "bg-orange-100 text-orange-700",
    "Closed": "bg-gray-100 text-gray-500",
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8">
      <div className="absolute inset-0 bg-[#0A1628]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-5 flex items-start justify-between gap-4 z-10">
          <div className="flex-1">
            <div className="flex flex-wrap gap-2 mb-2">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#0A1628]/10 text-[#00C2C2]">{tender.sector}</span>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusColor[tender.status]}`}>{tender.status}</span>
            </div>
            <h2 className="text-lg font-bold text-[#0A1628] leading-snug">{tender.title}</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer shrink-0">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-6 py-6">
          <p className="text-gray-600 text-sm leading-relaxed mb-6">{tender.description}</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {[
              { icon: "ri-government-line", label: t("listColSector"), value: tender.client },
              { icon: "ri-map-pin-2-line", label: t("listColCountry"), value: tender.country },
              { icon: "ri-file-list-3-line", label: "Reference", value: tender.reference },
              { icon: "ri-money-dollar-circle-line", label: "Budget", value: formatBudget(tender.budget) },
              { icon: "ri-calendar-close-line", label: t("listColDeadline"), value: new Date(tender.deadline).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) },
            ].map((item) => (
              <div key={item.label} className="bg-[#f8f9fc] rounded-lg p-3 border border-gray-100">
                <div className="w-6 h-6 flex items-center justify-center text-[#00C2C2] mb-1.5"><i className={`${item.icon} text-sm`} /></div>
                <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-0.5">{item.label}</p>
                <p className="text-xs font-bold text-[#0A1628] leading-snug">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="bg-[#0A1628] rounded-lg p-5 text-center">
            <p className="text-white/70 text-sm mb-4">{t("modalNeedHelp")}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => {
                  onRequestSupport?.(tender.reference);
                  onClose();
                  setTimeout(() => {
                    document.getElementById("tender-support")?.scrollIntoView({ behavior: "smooth" });
                  }, 150);
                }}
                className="bg-[#0A1628] text-white text-sm font-bold px-6 py-2.5 rounded-lg hover:bg-[#0A1628] transition-colors whitespace-nowrap cursor-pointer"
              >
                {t("modalRequestSupport")}
              </button>
              <button
                onClick={() => {
                  onRequestSupport?.(tender.reference);
                  onClose();
                  setTimeout(() => {
                    document.getElementById("tender-support")?.scrollIntoView({ behavior: "smooth" });
                  }, 150);
                }}
                className="bg-white/10 border border-white/20 text-white text-sm font-semibold px-6 py-2.5 rounded-lg hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t("modalSubmitInterest")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
